/**
 * useVoiceRecognition.js — Clara STT Hook
 * CLARA-STT-ANDROID-DEDUPE-1
 *
 * STT-Policy:
 * - Desktop: Browser SpeechRecognition, continuous=true, Auto-Stop nach 3s Pause
 * - Android/Mobile: Browser SpeechRecognition, continuous=false (Push-to-talk, ein Satz)
 * - Fallback: Whisper Server via /api/whisper/transcribe
 *   → aktiviert bei: network/service-not-allowed Fehler, Browser-STT nicht verfügbar,
 *     oder Duplikations-Muster erkannt (>35% Wiederholungen)
 *
 * Duplicate-Guard: verhindert akkumulierte Wiederholungen wie
 * "was was kannst was kannst du was kannst du mir..."
 */

import { useState, useEffect, useRef, useCallback } from 'react';

const WHISPER_ENDPOINT = '/api/whisper/transcribe';
const WHISPER_LANG = 'de';

// ─────────────────────────────────────────────────────────────
// Duplicate-Guard: hängt nur an wenn der neue Text nicht bereits
// im bestehenden Transcript enthalten ist
// ─────────────────────────────────────────────────────────────
function appendUniqueTranscript(current, next) {
  const a = current.trim();
  const b = next.trim();

  if (!b) return a;
  if (!a) return b;
  if (a === b) return a;
  if (a.endsWith(b)) return a;       // b ist bereits am Ende von a
  if (b.startsWith(a)) return b;     // b enthält a vollständig → b ist die Erweiterung

  return `${a} ${b}`.trim();
}

// ─────────────────────────────────────────────────────────────
// Duplikations-Erkennung: prüft ob >35% der Wörter Wiederholungen sind
// ─────────────────────────────────────────────────────────────
function hasDuplicatePattern(text) {
  const words = text.trim().split(/\s+/);
  if (words.length < 6) return false;

  const seen = new Map();
  let duplicates = 0;
  for (const word of words) {
    const w = word.toLowerCase();
    seen.set(w, (seen.get(w) || 0) + 1);
    if (seen.get(w) > 1) duplicates++;
  }
  return (duplicates / words.length) > 0.35;
}

export const useVoiceRecognition = () => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const [mode, setMode] = useState('browser'); // 'browser' | 'whisper'

  const recognitionRef = useRef(null);
  const isMountedRef = useRef(true);
  const autoStopTimeoutRef = useRef(null);
  const accumulatedTranscriptRef = useRef('');

  // MediaRecorder für Whisper-Fallback
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  const isAndroid = /Android/i.test(navigator.userAgent);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // ─────────────────────────────────────────────────────────────
  // Whisper-Transkription
  // ─────────────────────────────────────────────────────────────
  const transcribeWithWhisper = useCallback(async (audioBlob) => {
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('language', WHISPER_LANG);

      const resp = await fetch(WHISPER_ENDPOINT, {
        method: 'POST',
        body: formData,
        signal: AbortSignal.timeout(30000),
      });

      if (!resp.ok) throw new Error(`Whisper HTTP ${resp.status}`);
      const data = await resp.json();
      const text = data.text || data.transcript || '';
      console.log('🎤 [Whisper] Transkription:', text);
      return text.trim();
    } catch (e) {
      console.error('🎤 [Whisper] Fehler:', e.message);
      return '';
    }
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Whisper-Modus: Aufnahme starten
  // ─────────────────────────────────────────────────────────────
  const startWhisperRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      audioChunksRef.current = [];

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm',
      });

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        if (!isMountedRef.current) return;
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const text = await transcribeWithWhisper(audioBlob);
        if (text && isMountedRef.current) {
          setTranscript(text);
          accumulatedTranscriptRef.current = text;
        }
        setIsListening(false);
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000);
      setIsListening(true);
      setError(null);
      accumulatedTranscriptRef.current = '';
      setTranscript('');
      console.log('🎤 [Whisper] Aufnahme gestartet');
    } catch (e) {
      console.error('🎤 [Whisper] Aufnahme-Fehler:', e.message);
      if (e.name === 'NotAllowedError') {
        setError('Mikrofon-Berechtigung verweigert. Bitte erlauben Sie den Zugriff.');
      } else {
        setError('Mikrofon nicht verfügbar.');
      }
      setIsListening(false);
    }
  }, [transcribeWithWhisper]);

  // ─────────────────────────────────────────────────────────────
  // Browser-STT Initialisierung
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    isMountedRef.current = true;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.warn('🎤 Browser-STT nicht verfügbar, Whisper-Modus aktiv');
      setMode('whisper');
      setIsSupported(true);
      return;
    }

    setIsSupported(true);
    setMode('browser');

    const recognition = new SpeechRecognition();

    // STT-Policy: Android = continuous=false (Push-to-talk, kein Duplikations-Bug)
    //             Desktop = continuous=true (freies Sprechen, Auto-Stop nach Pause)
    recognition.continuous = !isAndroid;
    recognition.interimResults = true;
    recognition.lang = 'de-DE';
    recognition.maxAlternatives = 1;

    console.log(`🎤 [Browser-STT] Policy: continuous=${!isAndroid}, isAndroid=${isAndroid}`);

    recognition.onstart = () => {
      if (!isMountedRef.current) return;
      setIsListening(true);
      setError(null);
      accumulatedTranscriptRef.current = '';
      console.log('🎤 [Browser-STT] Gestartet');
    };

    recognition.onresult = (event) => {
      if (!isMountedRef.current) return;

      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const t = result[0].transcript;
        if (result.isFinal) finalTranscript += t;
        else interimTranscript += t;
      }

      if (finalTranscript) {
        // Duplicate-Guard: hänge nur an wenn nicht bereits enthalten
        const updated = appendUniqueTranscript(accumulatedTranscriptRef.current, finalTranscript);
        accumulatedTranscriptRef.current = updated;
        setTranscript(updated);

        // Duplikations-Muster erkannt → Whisper-Fallback
        if (hasDuplicatePattern(updated)) {
          console.warn('🎤 [Browser-STT] Duplikations-Muster erkannt — wechsle zu Whisper');
          try { recognition.stop(); } catch (e) { /* ignore */ }
          setMode('whisper');
          setTranscript('');
          accumulatedTranscriptRef.current = '';
          return;
        }

        // Desktop: Auto-Stop nach 3s Pause
        if (!isMobile) {
          if (autoStopTimeoutRef.current) clearTimeout(autoStopTimeoutRef.current);
          autoStopTimeoutRef.current = setTimeout(() => {
            if (recognitionRef.current && isMountedRef.current) {
              try { recognitionRef.current.stop(); } catch (e) { /* ignore */ }
            }
          }, 3000);
        }
      } else if (interimTranscript) {
        setTranscript(accumulatedTranscriptRef.current + interimTranscript);
        if (autoStopTimeoutRef.current) {
          clearTimeout(autoStopTimeoutRef.current);
          autoStopTimeoutRef.current = null;
        }
      }
    };

    recognition.onerror = (event) => {
      if (!isMountedRef.current) return;
      if (autoStopTimeoutRef.current) clearTimeout(autoStopTimeoutRef.current);
      setIsListening(false);

      console.error('🎤 [Browser-STT] Fehler:', event.error);

      if (event.error === 'network' || event.error === 'service-not-allowed') {
        console.warn('🎤 Browser-STT Netzwerkfehler — wechsle zu Whisper');
        setMode('whisper');
        setError(null);
      } else if (event.error === 'not-allowed') {
        setError('Mikrofon-Berechtigung verweigert. Bitte erlauben Sie den Zugriff.');
      } else if (event.error === 'audio-capture') {
        setError('Mikrofon nicht verfügbar.');
      } else if (event.error === 'no-speech' || event.error === 'aborted') {
        setError(null);
      } else {
        setError(null);
      }
    };

    recognition.onend = () => {
      if (autoStopTimeoutRef.current) clearTimeout(autoStopTimeoutRef.current);
      if (isMountedRef.current) {
        setIsListening(false);
        if (accumulatedTranscriptRef.current.trim()) {
          setTranscript(accumulatedTranscriptRef.current.trim());
        }
      }
    };

    recognitionRef.current = recognition;

    return () => {
      isMountedRef.current = false;
      if (autoStopTimeoutRef.current) clearTimeout(autoStopTimeoutRef.current);
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch (e) { /* ignore */ }
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try { mediaRecorderRef.current.stop(); } catch (e) { /* ignore */ }
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, [isAndroid, isMobile]);

  // ─────────────────────────────────────────────────────────────
  // Öffentliche API
  // ─────────────────────────────────────────────────────────────
  const startListening = useCallback(() => {
    if (isListening) return;
    setTranscript('');
    accumulatedTranscriptRef.current = '';

    if (mode === 'whisper') {
      startWhisperRecording();
    } else {
      if (!recognitionRef.current) return;
      try {
        recognitionRef.current.start();
      } catch (e) {
        console.warn('🎤 Browser-STT Start-Fehler:', e.message);
        setMode('whisper');
        startWhisperRecording();
      }
    }
  }, [isListening, mode, startWhisperRecording]);

  const stopListening = useCallback(() => {
    if (autoStopTimeoutRef.current) clearTimeout(autoStopTimeoutRef.current);

    if (mode === 'whisper') {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    } else {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) { /* ignore */ }
      }
    }
  }, [mode]);

  const resetTranscript = useCallback(() => {
    setTranscript('');
    accumulatedTranscriptRef.current = '';
  }, []);

  return {
    isListening,
    transcript,
    error,
    isSupported,
    mode,
    startListening,
    stopListening,
    resetTranscript,
  };
};
